import { cn } from 'lib/utils'
import { Avatar, AvatarFallback } from 'components/ui/avatar'
import { PortfolioCircleAvatarProps, SIZE_MAP } from './types'

/**
 * Circular composite avatar for portfolios and portfolio-backed entities.
 *
 * Layout by image count (after filtering out missing imageUrls):
 *   0 → ShadCN Avatar + AvatarFallback (gray, fallbackInitial)
 *   1 → full circle, single image
 *   2+ → split circle, first two images side-by-side
 *
 * Mirrors the methodology used in the Home dashboard Portfolios card.
 */
export function PortfolioCircleAvatar({
    locations,
    fallbackInitial = 'P',
    size = 'md',
    className,
}: PortfolioCircleAvatarProps) {
    const sizePx = SIZE_MAP[size]
    const images = locations
        .map(loc => loc.imageUrl)
        .filter((url): url is string => Boolean(url))
        .slice(0, 2)

    if (images.length === 0) {
        return (
            <Avatar
                className={cn('shrink-0', className)}
                style={{ width: sizePx, height: sizePx }}
            >
                <AvatarFallback className="text-xs bg-gray-200 text-gray-500">
                    {fallbackInitial.charAt(0).toUpperCase()}
                </AvatarFallback>
            </Avatar>
        )
    }

    if (images.length === 1) {
        return (
            <div
                className={cn(
                    'shrink-0 rounded-full overflow-hidden bg-cover bg-center',
                    className,
                )}
                style={{
                    width: sizePx,
                    height: sizePx,
                    backgroundImage: `url(${images[0]})`,
                }}
                role="img"
                aria-label={locations[0]?.name || 'Location image'}
            />
        )
    }

    return (
        <div
            className={cn(
                'shrink-0 rounded-full overflow-hidden grid grid-cols-2 gap-[1px]',
                className,
            )}
            style={{ width: sizePx, height: sizePx }}
            role="img"
            aria-label={`${locations.length} location${locations.length === 1 ? '' : 's'}`}
        >
            {images.map((img, i) => (
                <div
                    key={i}
                    className="bg-cover bg-center"
                    style={{ backgroundImage: `url(${img})` }}
                />
            ))}
        </div>
    )
}
